// Components/EditApiChannelModal.tsx
import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import Select from "react-select";
import { updateApiChannel, fetchApiTypes } from "../Services/APIService";
import {
  ApiChannel,
  ApiChannelFormData,
  ApiType,
} from "../types/apiChannel.types";

interface EditApiChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ApiChannelFormData) => void;
  editApiChannelData: ApiChannel | null;
}

interface ValidationErrors {
  channelName?: string;
  apiTypeId?: string;
}

interface SelectOption {
  value: number;
  label: string;
}

const EditApiChannelModal: React.FC<EditApiChannelModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editApiChannelData,
}) => {
  const [formData, setFormData] = useState<ApiChannelFormData>({
    id: 0,
    channelName: "",
    apiTypeId: 0,
    tenantId: 1000,
    modifiedBy: 1000,
  });
  const [apiTypes, setApiTypes] = useState<ApiType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    const fetchTypes = async () => {
      setIsLoadingTypes(true);
      try {
        const types = await fetchApiTypes();
        setApiTypes(types);
      } catch (error) {
        toast.error("Failed to load API types");
      } finally {
        setIsLoadingTypes(false);
      }
    };
    fetchTypes();
  }, []);

  useEffect(() => {
    if (editApiChannelData && apiTypes.length > 0) {
      setFormData({
        id: editApiChannelData.id,
        channelName: editApiChannelData.channelName,
        apiTypeId: editApiChannelData.apiTypeId,
        tenantId: 1000,
        modifiedBy: 1000,
      });
    }
  }, [editApiChannelData, apiTypes]);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.channelName.trim()) {
      newErrors.channelName = "Channel name is required";
      isValid = false;
    }
    if (!formData.apiTypeId) {
      newErrors.apiTypeId = "API type is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleClearAll = () => {
    setFormData({
      channelName: "",
      apiTypeId: 0,
      tenantId: 1000,
      createdBy: 1000,
    });
    setErrors({});
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof ValidationErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleTypeChange = (selectedOption: SelectOption | null) => {
    setFormData((prev) => ({
      ...prev,
      apiTypeId: selectedOption ? selectedOption.value : 0,
    }));
    if (errors.apiTypeId) {
      setErrors((prev) => ({ ...prev, apiTypeId: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      //toast.error("Please fix the errors in the form");
      return;
    }

    try {
      const trimmedData = {
        ...formData,
        channelName: formData.channelName.trim().replace(/\s+/g, " "), // final trim
      };
      const result = await updateApiChannel(trimmedData);
      console.log("Response from updateApiChannel:", result);

      if (result) {
        onSubmit(formData);
        onClose();
      } else {
        toast.error("API channel update failed");
      }
    } catch (error: any) {
      toast.error(error.message || "Update failed");
    }
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <>
      <Toaster position="top-center" />
      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={onClose}
      ></div>
      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Edit API Channel</h5>
            <button className="btn-close-custom" onClick={onClose}>
              <span className="icon-close"></span>
            </button>
          </div>
          <div className="modal-body-custom">
            <div className="modal-body-content">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="channelName">
                      Channel Name{requiredField}
                    </label>
                    <div className="input-with-icon">
                      <span className="input-icon icon-communication"></span>
                      <input
                        type="text"
                        name="channelName"
                        id="channelName"
                        value={formData.channelName}
                        onChange={handleChange}
                        placeholder="Enter Channel Name"
                        maxLength={100}
                        className={`form-control-custom ${
                          errors.channelName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.channelName && (
                        <div className="invalid-feedback text-danger">
                          {errors.channelName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="apiTypeId">
                      API Type{requiredField}
                    </label>
                    <select
                      name="apiTypeId"
                      value={formData.apiTypeId}
                      onChange={handleChange}
                      className={`form-select form-control-lg ${
                        errors.apiTypeId ? "is-invalid" : ""
                      }`}
                    >
                      <option value="">Select API Type</option>
                      {apiTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.typeName}
                        </option>
                      ))}
                    </select>
                    {errors.apiTypeId && (
                      <div className="invalid-feedback text-danger">
                        {errors.apiTypeId}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer-custom">
              <button className="btn-custom btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn-custom btn-primary-custom"
                onClick={handleSubmit}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditApiChannelModal;
